import { ref, computed, type Ref, type ComputedRef } from 'vue';
import { nursesApi, type NurseProfileDto } from '@/services/api/nursesApi';

/**
 * Lazily fetches the nurse record for the signed-in user from `GET /nurses/me`
 * and caches it for the rest of the SPA lifetime.
 *
 * The auth payload is intentionally lean — it doesn't carry professional-record
 * fields like department / specialization / nurse id. Pages that need those
 * call this composable, which fires the request once on first use and serves
 * the cached value to every subsequent caller.
 *
 * Reset on logout via `resetCurrentNurse()` (called from the auth store).
 */

// Module-level singletons — every call to useCurrentNurse() reads/writes these
// so multiple components share one fetch and one in-memory record.
const profile = ref<NurseProfileDto | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);
let inflight: Promise<void> | null = null;

async function loadOnce(): Promise<void> {
  if (profile.value) return; // already cached
  if (inflight) return inflight; // de-dupe concurrent loads

  isLoading.value = true;
  error.value = null;
  inflight = nursesApi
    .getMine()
    .then((data) => {
      profile.value = data;
    })
    .catch((err: any) => {
      // 404 = the signed-in user has no nurse record (data drift). Surface
      // a soft error so screens can degrade gracefully without crashing.
      error.value = err?.response?.data?.message ?? 'Failed to load nurse profile';
    })
    .finally(() => {
      isLoading.value = false;
      inflight = null;
    });

  return inflight;
}

export function useCurrentNurse(): {
  profile: Ref<NurseProfileDto | null>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
  department: ComputedRef<string>;
  refresh: () => Promise<void>;
} {
  // Fire once per page that mounts a consumer — cached across calls.
  void loadOnce();

  const department = computed(() => profile.value?.department ?? '');

  const refresh = async () => {
    profile.value = null;
    inflight = null;
    await loadOnce();
  };

  return { profile, isLoading, error, department, refresh };
}

/** Clear the cached nurse profile — call from the auth store on logout. */
export function resetCurrentNurse(): void {
  profile.value = null;
  isLoading.value = false;
  error.value = null;
  inflight = null;
}
